import { odmien } from "../../lib/polski.js";
import type { ParametrWidoku, RaportOceny, StanParametru } from "../../lib/raportOceny.js";
import { Badge } from "../ui/Badge.js";
import { Spinner } from "../ui/Spinner.js";

/**
 * Lista wybranych parametrow oceny ze stanem kazdego z nich.
 *
 * To jest odpowiedz karty na pytanie "czy to w ogole zostalo ocenione": brak wskazan przy parametrze,
 * ktory sie policzyl, i brak wskazan przy parametrze, ktory sie nie policzyl, to dwie rozne rzeczy i
 * kazda ma tu wlasna plakietke z tekstem (A-7: nie sam kolor). Kolejnosc jest kolejnoscia metodyki.
 *
 * Plakietki mowia o stanie OBLICZENIA, nie o ocenie rozmowy, wiec zostaja na wariantach statusow (ST-1): "Oceniono"
 * success, "Nie policzona" danger. Przebieg nieudany (ucieta, niezgodna, odmowa) jest informacja z powodem pod spodem,
 * a nie ostrzezeniem: w widokach oceny bursztyn (--status-warning) znaczy "najlepsze" (zloto), wiec tu by mylil.
 */

function Stan({ stan, faza }: { stan: StanParametru; faza: RaportOceny["faza"] }): React.JSX.Element {
  switch (stan.rodzaj) {
    case "oceniono":
      return (
        <Badge wariant="success">
          {`Oceniono: ${stan.liczbaWskazan} ${odmien(stan.liczbaWskazan, "wskazanie", "wskazania", "wskazań")}`}
        </Badge>
      );
    case "brak-wskazan":
      return <Badge>Oceniono, brak wskazań</Badge>;
    case "nieudana":
      return <Badge wariant="info">{stan.etykieta}</Badge>;
    case "niepoliczona":
      return <Badge wariant="danger">Nie policzona</Badge>;
    case "w-toku":
      return (
        <span className="ocena-parametr__w-toku" role="status">
          <Spinner rozmiar="s" />
          {faza === "kolejka" ? "Czeka w kolejce" : "Liczy się"}
        </span>
      );
  }
}

function Opis({ parametr }: { parametr: ParametrWidoku }): React.JSX.Element | null {
  const { stan } = parametr;
  if (stan.rodzaj === "nieudana") {
    return (
      <p className="ocena-parametr__opis">
        {stan.opis}
        {stan.szczegol && <span className="ocena-parametr__szczegol">{` Szczegół: ${stan.szczegol}`}</span>}
      </p>
    );
  }
  if (stan.rodzaj === "niepoliczona") {
    return (
      <p className="ocena-parametr__opis">
        Nie ma wyniku tego parametru. Zleć ocenę jeszcze raz przyciskiem „Oceń ponownie”.
      </p>
    );
  }
  return null;
}

export function ListaParametrow({
  parametry,
  faza,
}: {
  parametry: readonly ParametrWidoku[];
  faza: RaportOceny["faza"];
}): React.JSX.Element {
  return (
    <ul className="ocena-parametry" aria-label="Stan oceny wybranych parametrów">
      {parametry.map((p) => (
        <li className="ocena-parametr" key={p.slug} data-stan={p.stan.rodzaj}>
          <div className="ocena-parametr__wiersz">
            <span className="ocena-parametr__tytul">{p.tytul}</span>
            <Stan stan={p.stan} faza={faza} />
          </div>
          <Opis parametr={p} />
        </li>
      ))}
    </ul>
  );
}
